import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Paper,
    Breadcrumbs,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Chip,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PowerSettingsNew as PowerSettingsNewIcon,
    NavigateNext as NavigateNextIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import { useNotification } from '../../../components/Notification';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import MultiSelect from '../../../components/MultiSelect';
import Select from '../../../components/Select';
import Switch from '../../../components/Switch';
import { formatDateTimeWithCulture } from '../../../utils/dateUtils';
import {
    fetchInit,
    fetchAlertConfigurations,
    fetchAlertConfigurationById,
    createAlertConfiguration,
    updateAlertConfiguration,
    deleteAlertConfiguration,
    toggleActive,
    selectAlertConfigurations,
    selectAlertConfigurationsLoading,
    selectAlertConfigurationsError,
    selectAlertConfigurationsSuccessMessage,
    selectAlertConfigurationsTotal,
    selectAlertConfigurationsPage,
    selectAlertConfigurationsPageSize,
    selectAlertConfigurationsSortColumn,
    selectAlertConfigurationsSortDirection,
    selectFilterAlertType,
    selectFilterIsActive,
    selectFilterUnitId,
    selectUnits,
    selectAlertTypes,
    selectNotificationMethods,
    setPage,
    setPageSize,
    setSortColumn,
    setSortDirection,
    setFilterAlertType,
    setFilterIsActive,
    setFilterUnitId,
    clearError,
    clearSuccessMessage,
    clearSelectedAlert,
} from './slice';
import './AlertConfigurations.css';

// Constants removed - using data from API instead

function AlertConfigurations() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const alertConfigurations = useSelector(selectAlertConfigurations);
    const loading = useSelector(selectAlertConfigurationsLoading);
    const error = useSelector(selectAlertConfigurationsError);
    const successMessage = useSelector(selectAlertConfigurationsSuccessMessage);
    const total = useSelector(selectAlertConfigurationsTotal);
    const page = useSelector(selectAlertConfigurationsPage);
    const pageSize = useSelector(selectAlertConfigurationsPageSize);
    const sortColumn = useSelector(selectAlertConfigurationsSortColumn);
    const sortDirection = useSelector(selectAlertConfigurationsSortDirection);
    const filterAlertType = useSelector(selectFilterAlertType);
    const filterIsActive = useSelector(selectFilterIsActive) ?? null;
    const filterUnitId = useSelector(selectFilterUnitId) ?? null;
    const units = useSelector(selectUnits);

    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        alertType: '',
        condition: '',
        unitId: null,
        notificationMethods: [],
        notificationRecipients: [],
        isActive: true,
    });

    // Fetch init data on mount
    useEffect(() => {
        dispatch(fetchInit());
    }, [dispatch]);

    // Fetch alert configurations when filters/pagination/sorting change
    useEffect(() => {
        const params = {
            page,
            pageSize,
            sortColumn,
            sortDirection,
        };

        if (filterAlertType) {
            params.alertType = filterAlertType;
        }

        if (filterIsActive !== null && filterIsActive !== undefined) {
            params.isActive = filterIsActive;
        }

        if (filterUnitId) {
            params.unitId = filterUnitId;
        }

        dispatch(fetchAlertConfigurations(params));
    }, [dispatch, page, pageSize, sortColumn, sortDirection, filterAlertType, filterIsActive, filterUnitId]);

    // Show error/success notifications
    useEffect(() => {
        if (error) {
            showNotification(error, 'error');
            dispatch(clearError());
        }
    }, [error, dispatch, showNotification]);

    useEffect(() => {
        if (successMessage) {
            showNotification(successMessage, 'success');
            dispatch(clearSuccessMessage());
            if (showAddDialog) setShowAddDialog(false);
            if (showEditDialog) setShowEditDialog(false);
            if (showDeleteDialog) setShowDeleteDialog(false);
            setFormData({
                name: '',
                description: '',
                alertType: '',
                condition: '',
                unitId: null,
                notificationMethods: [],
                notificationRecipients: [],
                isActive: true,
            });
        }
    }, [successMessage, dispatch, showNotification, showAddDialog, showEditDialog, showDeleteDialog]);

    // Handle pagination
    const handlePageChange = (newPage) => {
        dispatch(setPage(newPage));
    };

    const handlePageSizeChange = (newSize) => {
        dispatch(setPageSize(newSize));
    };

    // Handle sorting
    const handleSortChange = (column, direction) => {
        dispatch(setSortColumn(column));
        dispatch(setSortDirection(direction));
    };

    // Handle filters
    const handleFilterAlertTypeChange = (e) => {
        dispatch(setFilterAlertType(e.target.value || ''));
    };

    const handleFilterIsActiveChange = (e) => {
        const value = e.target.value;
        dispatch(setFilterIsActive(value === '' ? null : value === 'true'));
    };

    const handleFilterUnitIdChange = (e) => {
        dispatch(setFilterUnitId(e.target.value || null));
    };

    // Handle add
    const handleAddClick = () => {
        setFormData({
            name: '',
            description: '',
            alertType: '',
            condition: '',
            unitId: null,
            notificationMethods: [],
            notificationRecipients: [],
            isActive: true,
        });
        setShowAddDialog(true);
    };

    // Handle edit
    const handleEditClick = (alert) => {
        setFormData({
            name: alert.name || '',
            description: alert.description || '',
            alertType: alert.alertType || '',
            condition: alert.condition || '',
            unitId: alert.unitId || null,
            notificationMethods: alert.notificationMethods || [],
            notificationRecipients: Array.isArray(alert.notificationRecipients)
                ? alert.notificationRecipients.join(', ')
                : (alert.notificationRecipients || ''),
            isActive: alert.isActive !== undefined ? alert.isActive : true,
        });
        setSelectedAlert(alert);
        setShowEditDialog(true);
    };

    // Handle delete
    const handleDeleteClick = (alert) => {
        setSelectedAlert(alert);
        setShowDeleteDialog(true);
    };

    // Handle toggle active
    const handleToggleActive = (alert) => {
        dispatch(toggleActive(alert.id));
    };

    // Handle form submit
    const handleSubmit = () => {
        const alertData = {
            ...formData,
            notificationRecipients: typeof formData.notificationRecipients === 'string'
                ? formData.notificationRecipients.split(',').map(r => r.trim()).filter(r => r)
                : Array.isArray(formData.notificationRecipients)
                    ? formData.notificationRecipients
                    : [],
        };

        if (selectedAlert) {
            dispatch(updateAlertConfiguration({ id: selectedAlert.id, alert: alertData }));
        } else {
            dispatch(createAlertConfiguration(alertData));
        }
    };

    // Table columns
    const columns = [
        {
            key: 'id',
            label: t('alertConfigurations.table.id'),
            align: 'left',
        },
        {
            key: 'name',
            label: t('alertConfigurations.table.name'),
            align: 'left',
        },
        {
            key: 'alertType',
            label: t('alertConfigurations.table.alertType'),
            render: (value) => {
                const type = alertTypes.find(t => t.id === value);
                return type ? type.name : value;
            },
        },
        {
            key: 'unitName',
            label: t('alertConfigurations.table.unit'),
            render: (value) => value || '-',
        },
        {
            key: 'notificationMethods',
            label: t('alertConfigurations.table.notificationMethods'),
            render: (value) => {
                if (!value || !Array.isArray(value) || value.length === 0) return '-';
                return (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {value.map((method, idx) => {
                            const methodObj = notificationMethods.find(m => m.id === method);
                            return (
                                <Chip
                                    key={idx}
                                    label={methodObj ? methodObj.name : method}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            );
                        })}
                    </Box>
                );
            },
        },
        {
            key: 'isActive',
            label: t('alertConfigurations.table.isActive'),
            render: (value) => (
                <Chip
                    label={value ? t('alertConfigurations.status.active') : t('alertConfigurations.status.inactive')}
                    color={value ? 'success' : 'default'}
                    size="small"
                />
            ),
        },
        {
            key: 'lastTriggeredOn',
            label: t('alertConfigurations.table.lastTriggeredOn'),
            render: (value) => value ? formatDateTimeWithCulture(value, i18n.language) : '-',
        },
        {
            key: 'triggerCount',
            label: t('alertConfigurations.table.triggerCount'),
            align: 'right',
        },
        {
            key: 'actions',
            label: t('alertConfigurations.table.actions'),
            align: 'center',
            render: (value, row) => (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Tooltip title={t('alertConfigurations.editButton')}>
                        <IconButton size="small" onClick={() => handleEditClick(row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('alertConfigurations.toggleActive')}>
                        <IconButton
                            size="small"
                            onClick={() => handleToggleActive(row)}
                            color={row.isActive ? 'success' : 'default'}
                        >
                            <PowerSettingsNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('alertConfigurations.deleteButton')}>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(row)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    // Debug: Log units to check if they're loaded
    useEffect(() => {
        console.log('[AlertConfigurations] Units loaded:', units.length, units);
    }, [units]);

    const alertTypes = useSelector(selectAlertTypes);
    const notificationMethods = useSelector(selectNotificationMethods);

    const unitsOptions = units.map(u => ({ id: u.id, name: u.name }));
    // Use data from API instead of constants
    const alertTypeOptions = alertTypes.map(t => ({ id: t.id, name: t.name }));
    const notificationMethodOptions = notificationMethods.map(m => ({ id: m.id, name: m.name }));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header Section */}
            <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <WarningIcon fontSize="small" />
                        <Typography variant="body2">{t('alertConfigurations.breadcrumb.system')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">
                        {t('alertConfigurations.breadcrumb.alertConfigurations')}
                    </Typography>
                </Breadcrumbs>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold">
                        {t('alertConfigurations.title')}
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddClick}
                    >
                        {t('alertConfigurations.addButton')}
                    </Button>
                </Box>
            </Box>

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}
            >
                {/* Filter Section */}
                <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Select
                            label={t('alertConfigurations.filterByAlertType')}
                            value={filterAlertType}
                            onChange={handleFilterAlertTypeChange}
                            options={alertTypeOptions.map(opt => ({ id: opt.id, name: t(opt.name) }))}
                            placeholder={t('alertConfigurations.filterByAlertTypePlaceholder')}
                            sx={{ width: '250px' }}
                        />
                        <Select
                            label={t('alertConfigurations.filterByStatus')}
                            value={filterIsActive === null || filterIsActive === undefined ? '' : String(filterIsActive)}
                            onChange={handleFilterIsActiveChange}
                            options={[
                                { id: 'true', name: t('alertConfigurations.status.active') },
                                { id: 'false', name: t('alertConfigurations.status.inactive') },
                            ]}
                            placeholder={t('alertConfigurations.filterByStatusPlaceholder')}
                            sx={{ width: '200px' }}
                        />
                        <Select
                            label={t('alertConfigurations.filterByUnit')}
                            value={filterUnitId || ''}
                            onChange={handleFilterUnitIdChange}
                            options={unitsOptions}
                            placeholder={t('alertConfigurations.filterByUnitPlaceholder')}
                            sx={{ width: '250px' }}
                        />
                    </Box>
                </Box>

                {/* Table */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3 }}>
                    <Table
                        id="alert-configurations-table"
                        columns={columns}
                        data={alertConfigurations}
                        selectable={false}
                        loading={loading}
                        emptyMessage={t('alertConfigurations.noData')}
                        idField="id"
                        pagination={true}
                        page={page}
                        pageSize={pageSize}
                        totalItems={total}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        pageSizeOptions={[5, 10, 20, 50, 100]}
                        sortable={true}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                    />
                </Box>
            </Paper>

            {/* Add/Edit Dialog */}
            <Dialog open={showAddDialog || showEditDialog} onClose={() => { setShowAddDialog(false); setShowEditDialog(false); }} maxWidth="md" fullWidth>
                <DialogTitle>
                    {selectedAlert ? t('alertConfigurations.dialog.editTitle') : t('alertConfigurations.dialog.addTitle')}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        <Input
                            label={t('alertConfigurations.form.name')}
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder={t('alertConfigurations.form.namePlaceholder')}
                            required
                        />
                        <Input
                            label={t('alertConfigurations.form.description')}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder={t('alertConfigurations.form.descriptionPlaceholder')}
                            multiline
                            rows={3}
                        />
                        <Select
                            label={t('alertConfigurations.form.alertType')}
                            value={formData.alertType}
                            onChange={(e) => setFormData({ ...formData, alertType: e.target.value })}
                            options={alertTypeOptions.map(opt => ({ id: opt.id, name: t(opt.name) }))}
                            placeholder={t('alertConfigurations.form.alertTypePlaceholder')}
                            required
                        />
                        <Input
                            label={t('alertConfigurations.form.condition')}
                            value={formData.condition}
                            onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                            placeholder={t('alertConfigurations.form.conditionPlaceholder')}
                            multiline
                            rows={4}
                        />
                        <Select
                            label={t('alertConfigurations.form.unit')}
                            value={formData.unitId || ''}
                            onChange={(e) => setFormData({ ...formData, unitId: e.target.value || null })}
                            options={unitsOptions}
                            placeholder={t('alertConfigurations.form.unitPlaceholder')}
                        />
                        <MultiSelect
                            label={t('alertConfigurations.form.notificationMethods')}
                            value={formData.notificationMethods}
                            onChange={(e) => setFormData({ ...formData, notificationMethods: e.target.value })}
                            options={notificationMethodOptions.map(opt => ({ id: opt.id, name: t(opt.name) }))}
                            placeholder={t('alertConfigurations.form.notificationMethodsPlaceholder')}
                        />
                        <Input
                            label={t('alertConfigurations.form.notificationRecipients')}
                            value={Array.isArray(formData.notificationRecipients) 
                                ? formData.notificationRecipients.join(', ') 
                                : (formData.notificationRecipients || '')}
                            onChange={(e) => setFormData({ ...formData, notificationRecipients: e.target.value })}
                            placeholder={t('alertConfigurations.form.notificationRecipientsPlaceholder')}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Switch
                                checked={formData.isActive}
                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                            />
                            <Typography>{t('alertConfigurations.form.isActive')}</Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setShowAddDialog(false); setShowEditDialog(false); setSelectedAlert(null); }}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {t('common.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
                <DialogTitle>{t('alertConfigurations.dialog.deleteTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('alertConfigurations.dialog.deleteMessage', { name: selectedAlert?.name })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDeleteDialog(false)}>{t('common.cancel')}</Button>
                    <Button
                        onClick={() => {
                            dispatch(deleteAlertConfiguration(selectedAlert.id));
                        }}
                        variant="contained"
                        color="error"
                    >
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AlertConfigurations;

